/** The shape AuthProvider stores; only the two fields a handle can be built from are read here. */
interface StoredOperator {
  email?: string;
  name?: string;
}

/**
 * Who is at the keyboard. The API key authenticates the dashboard rather than the person, so writes
 * that record an author carry the handle instead — the same one a comment body mentions: `@minchul`.
 *
 * This is attribution, not authentication: the handle comes from browser storage an operator could
 * edit, exactly as the comment hub's star and read watermark already do. Crediting a note to someone
 * else needs a server-verified principal, which this API key scheme has no place to put yet.
 */
export class OperatorIdentity {
  private static readonly STORAGE_KEY = 'user';

  /** The handle for an account: the address without its domain, or the display name when there is none. */
  public static handleOf(email: string | undefined, name: string | undefined): string | undefined {
    const handle = (email ?? '').split('@')[0] || (name ?? '');
    return handle ? handle.toLowerCase() : undefined;
  }

  /** The signed-in operator, or nobody — an unreadable or absent account leaves the API key as author. */
  public static current(): string | undefined {
    const stored = localStorage.getItem(OperatorIdentity.STORAGE_KEY);
    if (!stored) {
      return undefined;
    }
    try {
      const operator = JSON.parse(stored) as StoredOperator;
      return OperatorIdentity.handleOf(operator.email, operator.name);
    } catch {
      return undefined;
    }
  }
}
